import { useEffect, useRef, useState } from "react";
import { Car, FileUp, ImageUp, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, fetchBlobUrl, notifyErr } from "@/lib/api";

export function VehiclePhoto({ v, className, fallback }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (v.photo_id) fetchBlobUrl(`/vehicles/${v.id}/photo`).then((u) => { url = u; setSrc(u); }).catch(() => setSrc(null));
    else setSrc(v.image_url || null);
    return () => url && URL.revokeObjectURL(url);
  }, [v.id, v.photo_id, v.image_url]);
  if (!src) return fallback || <Car size={18} className="text-zinc-500" />;
  return <img src={src} alt={v.model || ""} className={`block object-center ${className}`} data-testid={`vehicle-photo-${v.id}`} />;
}

const thumbs = new Map();
const thumbUrl = (vid, key) => {
  const k = `${vid}:${key}`;
  if (!thumbs.has(k)) thumbs.set(k, fetchBlobUrl(`/vehicles/${vid}/photo?thumb=true`).catch(() => { thumbs.delete(k); return null; }));
  return thumbs.get(k);
};

export function VehicleThumb({ vid, photoKey, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let on = true;
    if (vid && photoKey) thumbUrl(vid, photoKey).then((u) => { if (on) setSrc(u); });
    else setSrc(null);
    return () => { on = false; };
  }, [vid, photoKey]);
  return <span className="inline-grid place-items-center w-10 h-7 shrink-0 rounded bg-zinc-900 border border-zinc-800 overflow-hidden align-middle" data-testid={testid}>
    {src ? <img src={src} alt="" className="w-full h-full object-contain" /> : <Car size={14} className="text-zinc-600" />}</span>;
}

export async function generateAiPhoto(v, onDone) {
  const t = toast.loading(`Creo l'immagine AI di ${v.brand} ${v.model}…`);
  try { await api.post(`/vehicles/${v.id}/ai-photo`); toast.success("Immagine AI del veicolo pronta", { id: t }); onDone && onDone(); }
  catch (e) { toast.dismiss(t); notifyErr(e); }
}

function FileButton({ accept, onFile, testid, icon: Icon, children }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { await onFile(f); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <Button variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid={testid}>
        {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Icon size={15} className="mr-1" />}{children}</Button>
    </>
  );
}

export function VehicleUploads({ v, onDone, ai }) {
  const [busy, setBusy] = useState(false);
  const regen = async () => { setBusy(true); await generateAiPhoto(v, onDone); setBusy(false); };
  const send = async (path, f, msg) => {
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(path, fd); toast.success(msg); onDone(); } catch (e) { notifyErr(e); }
  };
  const libretto = (f) => {
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) { toast.error("Il libretto deve essere un file PDF"); return null; }
    return send(`/vehicles/${v.id}/libretto`, f, "Libretto caricato: verrà allegato al contratto");
  };
  return (
    <>
      <FileButton accept="application/pdf,.pdf" onFile={libretto} testid="upload-libretto-btn" icon={FileUp}>Carica libretto (PDF)</FileButton>
      <FileButton accept="image/*,.heic,.heif" onFile={(f) => send(`/vehicles/${v.id}/photo`, f, "Foto auto caricata")} testid="upload-photo-btn" icon={ImageUp}>Carica foto auto</FileButton>
      {ai && <Button variant="outline" disabled={busy || !v.brand || !v.model} onClick={regen} data-testid="ai-photo-btn">
        {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />}{v.photo_id ? "Rigenera immagine AI" : "Crea immagine AI"}</Button>}
    </>
  );
}
